import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { useCurrency } from '../i18n/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { localizeLangName } from '../i18n/languageNameMap';
import { pickDisplayLang, langCodeToEnglishName } from '../i18n/pickDisplayLang';
import { NonAffiliatedModal } from './NonAffiliatedModal';
import { Star, Clock, Heart, MapPin, Globe, ChevronDown, User, Headphones, ExternalLink } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { track, affiliateUrl } from '../lib/analytics';
import { toApiUploads } from '../lib/uploadsUrl';
import { formatDuration } from '../lib/formatDuration';
import { useAffiliateProviders } from '../context/AffiliateProvidersContext';

/**
 * ExperienceCard - Reusable experience card component with flip animation, image carousel, 
 * wishlist, dropdown, and branded affiliate buttons
 * 
 * @param {Object} exp - Experience data object
 * @param {string} testIdPrefix - Prefix for data-testid attributes (e.g., "experience", "trending", "place")
 * @param {string} containerClassName - CSS classes for the card container (for different layouts)
 * @param {Object} containerStyle - Inline styles for the card container
 * @param {number} animationDelay - Animation delay for stagger effect
 */
export const ExperienceCard = React.memo(function ExperienceCard({
  exp,
  testIdPrefix = 'experience',
  containerClassName = 'h-[420px]',
  containerStyle = {},
  animationDelay = 0,
  selected = false,
  // Sentinel that any parent can change to force the card's transient
  // UI state (flipped, carousel index, open dropdown / modal) back to
  // defaults — without re-mounting the component (which would drop the
  // entrance animation and image prefetch cache). Used by ExperiencesPage
  // to reset cards whenever filters/search change.
  resetSignal = null,
}) {
  const { lang, t, localized } = useLanguage();
  const { getProvider } = useAffiliateProviders();
  const { formatPrice, currency } = useCurrency();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [animatingHeart, setAnimatingHeart] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  // Reset all transient state when the parent bumps `resetSignal`. Skip the
  // initial mount (resetSignal === null or first run) so we don't clobber
  // the natural defaults; only react to actual changes.
  const isFirstResetRun = React.useRef(true);
  useEffect(() => {
    if (isFirstResetRun.current) {
      isFirstResetRun.current = false;
      return;
    }
    setFlipped(false);
    setCurrentImageIndex(0);
    setLanguageDropdownOpen(false);
    setContactModalOpen(false);
  }, [resetSignal]);
  // Track whether the currently-visible image has finished loading. Resets
  // on every carousel switch so each image gets its own fade-in. Used to
  // crossfade from the LQIP-style blurred placeholder to the sharp image.
  const [imgLoaded, setImgLoaded] = useState(false);
  // Sharp <img> ref — used by the effect below to short-circuit the blur
  // crossfade when the browser already has the image cached (in which case
  // the `load` event fires before React attaches `onLoad`, leaving the card
  // permanently blurred).
  const imgRef = React.useRef(null);
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      // Cache hit — image is already painted by the browser; skip the fade.
      setImgLoaded(true);
    } else {
      setImgLoaded(false);
    }
  }, [currentImageIndex]);

  // Desktop pre-fetch — when the user hovers the card we eagerly load the
  // NEXT carousel image (just one) after a 150 ms debounce so passing the
  // mouse over a grid of cards during scroll doesn't trigger an image storm.
  const prefetched = React.useRef(new Set());
  const prefetchTimer = React.useRef(null);
  const prefetchAll = () => {
    if (prefetchTimer.current) return; // already scheduled
    prefetchTimer.current = setTimeout(() => {
      prefetchTimer.current = null;
      const all = [exp.image, ...(exp.images || [])].filter(Boolean);
      const nextIdx = (currentImageIndex + 1) % Math.max(all.length, 1);
      const src = all[nextIdx];
      if (!src || prefetched.current.has(src)) return;
      const i = new Image();
      i.src = src;
      prefetched.current.add(src);
    }, 150);
  };
  const cancelPrefetch = () => {
    if (prefetchTimer.current) {
      clearTimeout(prefetchTimer.current);
      prefetchTimer.current = null;
    }
  };
  useEffect(() => () => cancelPrefetch(), []);

  // Non-affiliated mode: Book Now opens the in-app contact popup, the
  // affiliate logo on the flipside is replaced with a Location button that
  // opens Google Maps. Falls back to the standard affiliate flow when off.
  const isNonAffiliated = !!exp.non_affiliated;
  const mapsUrl = exp.non_affiliated_location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(exp.non_affiliated_location)}`
    : null;
  const handleContactOpen = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    track('book_now_click', {
      experience_id: exp.id,
      provider: 'non_affiliated',
    });
    setContactModalOpen(true);
  };

  const handleWishlistClick = (e) => {
    e.stopPropagation();
    toggleWishlist(exp);
    // Subtle haptic tap on supported devices (mobile only — desktop browsers
    // ignore navigator.vibrate). Matches the heart pop animation timing.
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      try { navigator.vibrate(15); } catch (_) { /* noop */ }
    }
    // Add pop animation
    setAnimatingHeart(true);
    setTimeout(() => {
      setAnimatingHeart(false);
    }, 400);
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    // Combine main image with additional images, filter out empty values.
    // Hard-cap the carousel at 5 images to keep the dot indicator readable.
    const imgs = [exp.image, ...(exp.images || [])].filter(Boolean).slice(0, 5);
    if (imgs.length > 1) {
      setCurrentImageIndex((currentImageIndex + 1) % imgs.length);
    }
    track('card_click', { experience_id: exp.id });
  };

  const getCurrentImage = () => {
    // Combine main image with additional images, filter out empty values (cap 5).
    const imgs = [exp.image, ...(exp.images || [])].filter(Boolean).slice(0, 5);
    // Route through /api/uploads for a 1-year immutable cache (idempotent for
    // URLs already stored as /api/uploads or external).
    return toApiUploads(imgs[currentImageIndex] || exp.image);
  };

  // Multi-variant image sets uploaded via the admin crop pipeline.
  // Parallel to `images`: index 0 = main image's set, then additional[*] (cap 5).
  const getCurrentImageSet = () => {
    const sets = [exp.image_set, ...(exp.image_sets || [])].slice(0, 5);
    const set = sets[currentImageIndex] || null;
    if (!set) return null;
    // Route each variant through /api/uploads for the 1-year immutable cache.
    return {
      ...set,
      avif_1x: toApiUploads(set.avif_1x),
      avif_2x: toApiUploads(set.avif_2x),
      webp_1x: toApiUploads(set.webp_1x),
      webp_2x: toApiUploads(set.webp_2x),
      jpeg_1x: toApiUploads(set.jpeg_1x),
    };
  };

  const handleFlipClick = (e) => {
    e.stopPropagation();
    setFlipped(!flipped);
    track('card_flip', { experience_id: exp.id });
  };

  const handleBackClick = (e) => {
    e.stopPropagation();
    setFlipped(false);
  };

  // Close dropdown on scroll — listener is only attached WHILE the dropdown
  // is open, so the Explore grid of 72 cards doesn't register 72 idle scroll
  // listeners that fire on every scroll tick.
  useEffect(() => {
    if (!languageDropdownOpen) return undefined;
    const handleScroll = () => setLanguageDropdownOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [languageDropdownOpen]);

  // Combine main image with additional images for carousel (cap at 5)
  const images = [exp.image, ...(exp.images || [])].filter(Boolean).slice(0, 5);

  return (
    <div
      className={`card-entrance ${selected ? 'card-selected ' : ''}${containerClassName}`}
      style={{ perspective: '1000px', animationDelay: `${animationDelay}s`, ...containerStyle }}
      data-testid={`${testIdPrefix}-card-${exp.id}`}
    >
      <div
        className={`card-flipper${flipped ? ' is-flipped' : ''}`}
      >
        {/* FRONT FACE */}
        <div
          className="card-face group absolute w-full h-full rounded-2xl border border-border bg-white overflow-hidden flex flex-col"
          style={{
            // translateZ(0) keeps the front face on its own GPU layer
            // throughout the rotation so it never blanks out mid-flip.
            transform: 'translateZ(0)',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          }}
          onMouseEnter={prefetchAll}
          onMouseLeave={cancelPrefetch}
        >
          {/* Image */}
          <div className="relative shrink-0 overflow-hidden">
            <div 
              onClick={handleImageClick}
              className="cursor-pointer relative aspect-[297/176] w-full"
            >
              {/* LQIP / blurred placeholder — sits behind the real image and
                  fades out once the sharp image has loaded. Uses the same
                  source with heavy blur so the dominant colors are present
                  while the high-res JPEG arrives. */}
              <img
                key={`lqip-${currentImageIndex}-${getCurrentImage()}`}
                src={getCurrentImage()}
                alt=""
                aria-hidden="true"
                width="594"
                height="352"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${imgLoaded ? 'opacity-0' : 'opacity-100'}`}
                style={{ filter: 'blur(18px)', transform: 'scale(1.08)' }}
              />
              {(() => {
                const set = getCurrentImageSet();
                const fallback = getCurrentImage();
                const alt = localized(exp, 'title');
                const onLoad = () => setImgLoaded(true);
                const baseClass = `relative h-full w-full object-cover transition-[transform,opacity] duration-500 group-hover:scale-105 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`;
                if (set && (set.avif_1x || set.webp_1x || set.jpeg_1x)) {
                  return (
                    <picture>
                      {set.avif_1x && set.avif_2x && (
                        <source type="image/avif" srcSet={`${set.avif_1x} 1x, ${set.avif_2x} 2x`} />
                      )}
                      {set.avif_1x && !set.avif_2x && (
                        <source type="image/avif" srcSet={set.avif_1x} />
                      )}
                      {set.webp_1x && set.webp_2x && (
                        <source type="image/webp" srcSet={`${set.webp_1x} 1x, ${set.webp_2x} 2x`} />
                      )}
                      {set.webp_1x && !set.webp_2x && (
                        <source type="image/webp" srcSet={set.webp_1x} />
                      )}
                      <img
                        ref={imgRef}
                        src={set.jpeg_1x || set.webp_1x || fallback}
                        alt={alt}
                        width="594"
                        height="352"
                        loading="lazy"
                        onLoad={onLoad}
                        className={baseClass}
                      />
                    </picture>
                  );
                }
                return (
                  <img
                    ref={imgRef}
                    src={fallback}
                    alt={alt}
                    width="594"
                    height="352"
                    loading="lazy"
                    onLoad={onLoad}
                    className={baseClass}
                  />
                );
              })()}
            </div>
            {exp.limited_event && (
              <Badge className="badge-drop absolute top-3 left-3 rounded-full bg-[#dc2626] text-white border-0 font-semibold text-xs">
                {exp.event_date ? (
                  <>
                    {t('limited_event')} • {new Date(exp.event_date).toLocaleDateString(lang, { month: 'short', day: 'numeric' })}
                    {exp.event_time && ` • ${exp.event_time}`}
                  </>
                ) : (
                  t('limited_event')
                )}
              </Badge>
            )}
            {selected && (
              <Badge
                className="badge-drop absolute top-3 left-3 rounded-full bg-[#0184ff] text-white border-0 font-semibold text-xs"
                data-testid={`${testIdPrefix}-selected-badge-${exp.id}`}
              >
                {t('selected_badge')}
              </Badge>
            )}
            {exp.featured && !exp.limited_event && !selected && (
              <Badge className="badge-drop absolute top-3 left-3 rounded-full bg-[#3c84ff] text-white border-0 font-semibold text-xs">
                {t('featured')}
              </Badge>
            )}
            {/* Image dots indicator (carousel + dots are both capped at 5) */}
            {images.length > 1 && (
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1.5">
                {images.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === currentImageIndex
                        ? 'bg-white w-4'
                        : 'bg-white/60 w-1.5'
                    }`}
                  />
                ))}
              </div>
            )}
            <button
              onClick={handleWishlistClick}
              className={`absolute top-3 right-3 transition-all duration-200 ${
                animatingHeart ? 'heart-pop' : ''
              }`}
              data-testid={`${testIdPrefix}-wishlist-btn-${exp.id}`}
            >
              <Heart
                className={`h-7 w-7 transition-all duration-200 ${
                  isInWishlist(exp.id) 
                    ? 'fill-red-500/50 text-red-500' 
                    : 'fill-none text-white stroke-[2.5px] drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]'
                }`}
              />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 flex-1 flex flex-col">
            {/* Title */}
            <h3 className="text-lg font-bold leading-snug line-clamp-2 tracking-wide text-[#1a4d7a]">
              {localized(exp, 'title')}
            </h3>
            
            {/* Teaser text */}
            <p className="text-sm font-medium mt-auto line-clamp-3" style={{ color: '#2d5f8d' }}>
              {localized(exp, 'description')}
            </p>
            
            {/* Rating at bottom left, Price at bottom right
                — always render the divider + reserved height even when
                both pieces are hidden, so cards stay visually aligned. */}
            {(() => {
              const hasRating =
                Number(exp.rating) > 0 && Number(exp.review_count) > 0;
              const isFree = !!exp.is_free;
              const hasPrice = isFree || Number(exp.price) > 0;
              return (
                <div className="pt-3 mt-auto border-t border-border flex items-center justify-between mb-3 min-h-[40px]">
                  {hasRating ? (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-[#ffcc2e] text-[#ffcc2e]" />
                      <span className="text-sm font-semibold text-[#1a4d7a]">{exp.rating}</span>
                      <span className="text-xs ml-1" style={{ color: '#2d5f8d' }}>({exp.review_count})</span>
                    </div>
                  ) : (
                    <span aria-hidden="true">&nbsp;</span>
                  )}
                  {hasPrice ? (
                    <div className="text-right">
                      {isFree ? (
                        <p className="font-bold text-lg text-[#1a4d7a]">
                          <span key={`free-${currency}`} className="price-flip">
                            {t('free_price')}
                          </span>
                        </p>
                      ) : (
                        <p className="text-sm">
                          <span style={{ color: '#2d5f8d' }}>{t('from_price')} </span>
                          <span
                            key={`price-${currency}`}
                            className="price-flip font-bold text-lg text-[#1a4d7a]"
                          >
                            {formatPrice(exp.price)}
                          </span>
                        </p>
                      )}
                    </div>
                  ) : (
                    <span aria-hidden="true">&nbsp;</span>
                  )}
                </div>
              );
            })()}
            
            {/* Buttons at bottom */}
            <div className="flex gap-2">
              <button
                onClick={handleFlipClick}
                className="flex-1 h-10 text-[13px] font-semibold text-[#00b5ff] bg-transparent rounded-full
                           border border-[#00b5ff]/40
                           hover:border-[#00b5ff] hover:bg-[#00b5ff]/5 active:scale-[0.97] transition-all duration-200"
              >
                {t('more_information')}
              </button>
              {isNonAffiliated ? (
                <button
                  type="button"
                  onClick={handleContactOpen}
                  className="flex-1 h-10 text-[13px] font-semibold text-white bg-[#3c84ff] rounded-full
                             hover:bg-[#2a6fe0] active:scale-[0.97] transition-all duration-200 flex items-center justify-center"
                  data-testid={`${testIdPrefix}-book-now-${exp.id}`}
                >
                  {t('book_now')}
                </button>
              ) : (
                <a
                  href={affiliateUrl(exp.affiliate_url, exp)}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  onClick={() => track('book_now_click', {
                    experience_id: exp.id,
                    provider: exp.affiliate_provider || 'unknown',
                  })}
                  className="flex-1 h-10 text-[13px] font-semibold text-white bg-[#3c84ff] rounded-full
                             hover:bg-[#2a6fe0] active:scale-[0.97] transition-all duration-200 flex items-center justify-center"
                >
                  {t('book_now')}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* BACK FACE - White background */}
        <div
          className="card-face absolute w-full h-full rounded-2xl border border-border bg-white overflow-hidden p-5 flex flex-col"
          style={{
            // translateZ(0) on the back keeps it composited as its own GPU
            // layer alongside the rotation; without it iOS Safari drops the
            // face for one frame at the 90° midpoint of the flip.
            transform: 'rotateY(180deg) translateZ(0)',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          }}
        >
          {/* Title */}
          <h3 className="text-base font-bold mb-2 line-clamp-2 text-[#1a4d7a]">
            {localized(exp, 'title')}
          </h3>
          
          {/* Quick info bar - Location, Duration, Language evenly spread */}
          <div className="flex items-center justify-between text-sm font-medium mb-3 pb-3 border-b border-border" style={{ color: '#2d5f8d' }}>
            <div className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              <span>{(() => {
                const key = `destination_${exp.destination}`;
                const txt = t(key);
                // `t()` returns the key itself when a translation is missing;
                // fall back to a capitalised version of the raw slug in that case.
                if (txt === key) {
                  return (exp.destination || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                }
                return txt;
              })()}</span>
            </div>
            {(() => {
              const dur = formatDuration(localized(exp, 'duration'), lang);
              if (!dur || !String(dur).trim()) return null;
              return (
                <div className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{dur}</span>
                </div>
              );
            })()}
            {((exp.live_guide_languages && exp.live_guide_languages.length > 0) || 
              (exp.audio_languages && exp.audio_languages.length > 0)) && (() => {
              const guideLangs = exp.live_guide_languages || [];
              const audioLangs = exp.audio_languages || [];
              // Pool of all languages available across both sections.
              const pool = Array.from(new Set([...guideLangs, ...audioLangs]));
              const displayLang = pickDisplayLang(pool, lang);
              return (
              <DropdownMenu 
                open={languageDropdownOpen} 
                onOpenChange={setLanguageDropdownOpen}
                modal={false}
              >
                <DropdownMenuTrigger 
                  onClick={(e) => {
                    // Don't bubble up — clicking the language button on the
                    // back face must not flip the card. We rely on Radix's
                    // own toggle for open/close so a second click while open
                    // closes the menu cleanly on desktop and mobile alike.
                    e.stopPropagation();
                  }}
                  className="flex items-center gap-1 border-none bg-transparent p-0 transition-colors focus-visible:outline-none data-[state=open]:text-inherit cursor-pointer"
                >
                    <Globe className="h-3.5 w-3.5" />
                    <span>
                      {localizeLangName(displayLang, lang)}
                    </span>
                    <ChevronDown className="h-3 w-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 z-[100]">
                  {guideLangs.length > 0 && (
                    <DropdownMenuItem className="flex items-start gap-2 py-2 focus:bg-transparent hover:bg-transparent">
                      <User className="h-4 w-4 mt-0.5 shrink-0 text-[#1a4d7a]" />
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-[#1a4d7a]">{t('live_tour_guide')}</span>
                        <span className="text-xs text-muted-foreground">
                          {guideLangs.map((n, idx) => {
                            const isMatch = n === displayLang;
                            return (
                              <React.Fragment key={n}>
                                <span className={isMatch ? 'font-bold text-[#1a4d7a]' : ''}>
                                  {localizeLangName(n, lang)}
                                </span>
                                {idx < guideLangs.length - 1 ? ', ' : ''}
                              </React.Fragment>
                            );
                          })}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {audioLangs.length > 0 && (
                    <DropdownMenuItem className="flex items-start gap-2 py-2 focus:bg-transparent hover:bg-transparent">
                      <Headphones className="h-4 w-4 mt-0.5 shrink-0 text-[#1a4d7a]" />
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-[#1a4d7a]">{t('audio_available_in')}</span>
                        <span className="text-xs text-muted-foreground">
                          {audioLangs.map((n, idx) => {
                            const isMatch = n === displayLang;
                            return (
                              <React.Fragment key={n}>
                                <span className={isMatch ? 'font-bold text-[#1a4d7a]' : ''}>
                                  {localizeLangName(n, lang)}
                                </span>
                                {idx < audioLangs.length - 1 ? ', ' : ''}
                              </React.Fragment>
                            );
                          })}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              );
            })()}
          </div>
          
          {/* What's included - Bullet points (Dynamic) */}
          {(() => {
            const hl = localized(exp, 'highlights');
            const list = Array.isArray(hl) ? hl : (exp.highlights_en || []);
            if (!list || list.length === 0) return <div className="flex-1"></div>;
            return (
              <div className="flex-1 mb-3">
                <ul className="text-sm font-medium space-y-2" style={{ color: '#2d5f8d' }}>
                  {list.map((highlight, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-[#0184ff] text-base leading-none mt-[2px] shrink-0">•</span>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
          
          {/* Full details — affiliate logo OR (when non-affiliated) a single
              Location button that opens Google Maps. */}
          <div className="mb-3">
            {isNonAffiliated ? (
              mapsUrl ? (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-9 flex items-center justify-center gap-2 px-4 rounded-lg bg-[#3c84ff] text-white text-sm font-semibold transition-all duration-200 hover:brightness-90 hover:-translate-y-0.5 hover:shadow-[0_8px_18px_rgba(15,59,99,0.18)] active:translate-y-0 active:scale-[0.97]"
                  data-testid={`${testIdPrefix}-location-btn-${exp.id}`}
                >
                  <MapPin className="h-4 w-4" />
                  {t('location')}
                </a>
              ) : null
            ) : (
              <>
                <p className="text-xs font-medium text-[#2d5f8d] mb-1.5 text-center" data-testid="more-details-at-label">
                  {t('more_details_at')}
                </p>
                {(() => {
                  const provider = getProvider(exp.affiliate_provider);
                  return (
                    <a
                      href={affiliateUrl(exp.affiliate_url, exp)}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      aria-label={`${t('view_on')} ${provider.name}`}
                      className="w-full h-9 flex items-center justify-center gap-2 px-4 rounded-lg transition-all duration-200 hover:brightness-90 hover:-translate-y-0.5 hover:shadow-[0_8px_18px_rgba(15,59,99,0.18)] active:translate-y-0 active:scale-[0.97]"
                      style={{ backgroundColor: provider.bg }}
                    >
                      {provider.logo && (
                        <img
                          src={provider.logo}
                          alt={provider.name}
                          className={provider.logoClass || ''}
                          style={
                            provider.custom && provider.logoHeight
                              ? { height: `${provider.logoHeight}px` }
                              : undefined
                          }
                        />
                      )}
                      {!provider.logo && (
                        <span className="text-white text-sm font-semibold">
                          {provider.name}
                        </span>
                      )}
                      <ExternalLink className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                    </a>
                  );
                })()}
              </>
            )}
          </div>
          
          {/* Buttons at bottom */}
          <div className="flex gap-2">
            <button
              onClick={handleBackClick}
              className="flex-1 h-10 text-[13px] font-semibold text-[#00b5ff] bg-transparent rounded-full
                         border border-[#00b5ff]/40
                         hover:border-[#00b5ff] hover:bg-[#00b5ff]/5 active:scale-[0.97] transition-all duration-200"
            >
              {t('back')}
            </button>
            {isNonAffiliated ? (
              <button
                type="button"
                onClick={handleContactOpen}
                className="flex-1 h-10 text-[13px] font-semibold text-white bg-[#3c84ff] rounded-full
                           hover:bg-[#2a6fe0] active:scale-[0.97] transition-all duration-200 flex items-center justify-center"
              >
                {t('book_now')}
              </button>
            ) : (
              <a
                href={affiliateUrl(exp.affiliate_url, exp)}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => track('book_now_click', {
                  experience_id: exp.id,
                  provider: exp.affiliate_provider || 'unknown',
                })}
                className="flex-1 h-10 text-[13px] font-semibold text-white bg-[#3c84ff] rounded-full
                           hover:bg-[#2a6fe0] active:scale-[0.97] transition-all duration-200 flex items-center justify-center"
              >
                {t('book_now')}
              </a>
            )}
          </div>
        </div>
      </div>
      <NonAffiliatedModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        text={localized(exp, 'non_affiliated_text')}
        phone={exp.non_affiliated_phone}
        email={exp.non_affiliated_email}
        web={exp.non_affiliated_web}
        location={exp.non_affiliated_location}
      />
    </div>
  );
}, (prev, next) => {
  // Custom equality: only re-render when something the card actually
  // depends on has changed. Ignores `containerStyle` reference (default
  // `{}` is a fresh object on every parent render). Cards keep their
  // `exp` ref stable across search-filter passes (the array changes but
  // the items in it don't), so this prevents the entire grid from
  // re-rendering on every keystroke.
  if (prev.exp !== next.exp) return false;
  if (prev.selected !== next.selected) return false;
  if (prev.testIdPrefix !== next.testIdPrefix) return false;
  if (prev.containerClassName !== next.containerClassName) return false;
  if (prev.animationDelay !== next.animationDelay) return false;
  // resetSignal: when the parent bumps it (e.g. filters changed), we must
  // re-render so the reset effect fires. Otherwise the card would skip
  // the render and keep its stale flipped/carousel state.
  if (prev.resetSignal !== next.resetSignal) return false;
  // containerStyle: shallow-compare its keys instead of reference. Most
  // callers don't pass it; the few that do pass simple flat objects.
  const ps = prev.containerStyle || {};
  const ns = next.containerStyle || {};
  const psKeys = Object.keys(ps);
  const nsKeys = Object.keys(ns);
  if (psKeys.length !== nsKeys.length) return false;
  for (const k of psKeys) if (ps[k] !== ns[k]) return false;
  return true;
});
